import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, Download, X, MessageSquare, Star } from "lucide-react";
import SurveyModal from '../../components/SurveyModal';
import DataDetailTable from '../../components/DataDetailTable';
import DataChecklistView from '../../components/DataChecklistView';
import MetadataEditor from '../../components/MetadataEditor/MetadataEditor';
import '../../styles/App.css';
import '../../styles/RekapDataDetails.css';
import config from '../../config/config';
import { useUnsealModal } from '../../hooks/useUnsealModal';
import UnsealProgressModal from '../../components/UnsealProgressModal';
import { getMenuItemsByRole } from '../../utils/menuItems';

const RekapDataDetailsPage = () => {
  const { no_tiket } = useParams();
  const [dataDetail, setDataDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catatan, setCatatan] = useState('');
  const [fileba, setFileba] = useState('');
  const [filerekom, setFilerekom] = useState('');
  const [verificationDocs, setVerificationDocs] = useState([]); // Changed to array
  const [isVisible, setIsVisible] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isChecklistViewOpen, setIsChecklistViewOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [checklistStatus, setChecklistStatus] = useState({});
  const [availableChecklists, setAvailableChecklists] = useState({});
  const [capaianData, setCapaianData] = useState([]);
  const [kepkaInfo, setKepkaInfo] = useState(null);
  const [isMetadataViewerOpen, setIsMetadataViewerOpen] = useState(false);
  const [metadataFileUrl, setMetadataFileUrl] = useState(null);
  const navigate = useNavigate();

  const handleChecklistViewOpen = (documentType) => {
    setSelectedDocumentType(documentType);
    setIsChecklistViewOpen(true);
  };

  const handleChecklistViewClose = () => {
    setIsChecklistViewOpen(false);
    setSelectedDocumentType('');
  };

  // Handle metadata viewer
  const handleViewMetadata = () => {
    if (dataDetail?.metadata && !dataDetail.metadata.toLowerCase().endsWith('.zip')) {
      const filename = dataDetail.metadata.split('/').pop();
      const metadataUrl = `${config.API_URL}/download/${btoa(filename)}`;
      setMetadataFileUrl(metadataUrl);
      setIsMetadataViewerOpen(true);
    }
  };

  const handleMetadataViewerClose = () => {
    setIsMetadataViewerOpen(false);
    setMetadataFileUrl(null);
  };

  // Fungsi untuk mengecek apakah ada data checklist
  const checkChecklistData = async (noTiket) => {
    if (!noTiket) return;

    const allDocumentTypes = ['peta', 'struktur_data', 'takel', 'dokumen', 'metadata', 'simbology', 'role', 'kualitas', 'cakupan'];
    const statusMap = {};
    const availabilityMap = {};

    // Initialize all document types
    allDocumentTypes.forEach(docType => {
      statusMap[docType] = false;
      availabilityMap[docType] = false;
    });

    const token = localStorage.getItem('token');

    // First, check which document types have checklist items
    try {
      const availabilityPromises = allDocumentTypes.map(async (docType) => {
        try {
          const response = await axios.get(
            `${config.API_URL}/api/checklist/${docType}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return { [docType]: response.data.checklist.length > 0 };
        } catch (error) {
          return { [docType]: false };
        }
      });

      const availabilityResults = await Promise.all(availabilityPromises);
      availabilityResults.forEach(result => Object.assign(availabilityMap, result));
      setAvailableChecklists(availabilityMap);
    } catch (error) {
      console.error('Error checking checklist availability:', error);
    }

    // Then check which documents have checklist data filled
    try {
      const promises = allDocumentTypes.map(async (docType) => {
        // Skip if no checklist available for this document type
        if (!availabilityMap[docType]) {
          return { [docType]: false };
        }
        try {
          const response = await fetch(`${config.API_URL}/api/checklist/data/${noTiket}/${docType}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.status === 404) {
            return { [docType]: false };
          }

          if (response.ok) {
            const data = await response.json();
            if (data && data.checklistData) {
              const items = data.checklistData.items || {};
              return { [docType]: Object.keys(items).length > 0 };
            }
          }

          return { [docType]: false };
        } catch (error) {
          // Silent handling
          return { [docType]: false };
        }
      });

      const results = await Promise.all(promises);
      results.forEach(result => Object.assign(statusMap, result));
      setChecklistStatus(statusMap);
    } catch (error) {
      // Silent handling
      setChecklistStatus(statusMap);
    }
  };
  const USE_EXTERNAL_SURVEY_LINK = false;
  const SURVEY_EXTERNAL_LINK = 'https://forms.gle/contohLinkKeGoogleFormAnda';

  // Fungsi untuk menangani scroll
  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const {
    isUnsealModalOpen,
    selectedTicket,
    closeUnsealModal,
    handleUnsealComplete
  } = useUnsealModal();

  // Fungsi untuk scroll ke atas
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const downloadVerificationDoc = async (docIndex = null) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${config.API_URL}/dataAdmin/downloadVerificationDoc/${btoa(dataDetail.no_tiket)}`;

      // Add docIndex to URL if specified
      if (docIndex !== null) {
        url += `/${docIndex}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Set filename based on whether it's a specific document or default
      const filename = docIndex !== null
        ? `Verifikasi_${dataDetail.no_tiket}_Catatan${docIndex}.pdf`
        : `Verifikasi_${dataDetail.no_tiket}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      let errorMessage = 'Gagal mengunduh dokumen verifikasi';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        if (error.response.data.details) {
          errorMessage += ` (Bucket: ${error.response.data.details.bucket}, Key: ${error.response.data.details.key})`;
        }
      }
      console.error(errorMessage);
    }
  };

  const handleSurveySubmit = (surveyData) => {
    console.log('Survey submitted:', surveyData);
    setSurveySubmitted(true);
    setShowSurvey(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        if (!token || !decoded.twoFactorVerified) {
          navigate('/');
          return;
        }
        setUserRole(decoded.role);

        const response = await axios.get(`${config.API_URL}/dataAdmin/details/${atob(no_tiket)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.data) {
          setDataDetail(response.data);
          checkChecklistData(response.data.no_tiket);
          setCatatan(response.data.datacatatan || '');
          setFileba(response.data.batte || '');
          setFilerekom(response.data.rekom || '');

          // Load capaian data
          if (response.data.capaian_data) {
            try {
              const parsedCapaian = typeof response.data.capaian_data === 'string'
                ? JSON.parse(response.data.capaian_data)
                : response.data.capaian_data;
              setCapaianData(parsedCapaian);
            } catch (error) {
              console.error('Error parsing capaian_data:', error);
              setCapaianData([]);
            }
          }

          // Load kepka info if available
          if (response.data.kepka_id) {
            try {
              const kepkaResponse = await axios.get(`${config.API_URL}/api/management/kepka/${response.data.kepka_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              // Backend sends { success: true, data: {...} }
              const kepkaData = kepkaResponse.data.data || kepkaResponse.data;
              if (kepkaData) {
                setKepkaInfo(kepkaData);
                console.log('✅ Kepka info loaded:', kepkaData);
              }
            } catch (error) {
              console.error('❌ Error fetching kepka info:', {
                kepka_id: response.data.kepka_id,
                status: error.response?.status,
                message: error.message
              });
              setKepkaInfo(null);
            }
          }

          // Handle multiple verification documents
          if (response.data.verification_notes && Array.isArray(response.data.verification_notes)) {
            setVerificationDocs(response.data.verification_notes);
          } else if (response.data.verification_note) {
            // Backward compatibility for single document
            setVerificationDocs([{
              index: 1,
              url: response.data.verification_note,
              name: 'Catatan 1'
            }]);
          } else {
            setVerificationDocs([]);
          }
        }
      } catch (err) {
        console.error('Error fetching data detail:', err);
        if (err.response && err.response.status === 404) {
          setError('Data dengan tiket ini sudah tidak ada atau telah dihapus.');
        } else {
          setError('Gagal memuat detail data. Silakan coba lagi.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, [no_tiket, navigate]);

  const getRoleName = () => {
    switch(userRole) {
      case 0: return 'Admin';
      case 1: return 'KL';
      case 2: return 'Supervisor';
      case 3: return 'Publikator';
      default: return 'User';
    }
  };

  const menuItems = useMemo(() => {
    return userRole !== null ? getMenuItemsByRole(userRole) : [];
  }, [userRole]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Memuat data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems} userRole={getRoleName()}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href={userRole === 0 || userRole === 2 ? '/admin' : userRole === 1 ? '/kl' : '/publikator'}>
                Dashboard
              </a>
            </li>
            <li className="breadcrumb-item">
              <a href={userRole === 0 || userRole === 2 ? '/admin/data' : '/kl/data'}>
                Daftar Data
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Rekap Data - {dataDetail?.no_tiket}
            </li>
          </ol>
        </nav>
        {dataDetail && (
          <div className="rekap-detail-wrapper">
            {/* Header Card */}
            <div className="rekap-header-card">
              <button onClick={() => navigate(-1)} className="close-button">
                <X size={20} />
              </button>
              <div className="header-content">
                <h2 className="rekap-title">
                  Detail Rekap Data Verifikasi
                </h2>
                <div className="ticket-info">
                  <span className="ticket-label">No. Tiket:</span>
                  <span className="ticket-number">{dataDetail.no_tiket}</span>
                </div>
                <div className="header-actions">
                  {USE_EXTERNAL_SURVEY_LINK ? (
                    <a
                      href={SURVEY_EXTERNAL_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="survey-button"
                    >
                      <MessageSquare size={16} />
                      Berikan Feedback
                    </a>
                  ) : (
                    <button
                      className="survey-button"
                      onClick={() => setShowSurvey(true)}
                      disabled={surveySubmitted}
                    >
                      {surveySubmitted ? (
                        <>
                          <Star size={16} />
                          Survey Terkirim
                        </>
                      ) : (
                        <>
                          <MessageSquare size={16} />
                          Berikan Feedback
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Status Summary */}
            <div className="status-summary-card">
              <div className="summary-grid">
                <div className="summary-item">
                  <label>Nama IGT</label>
                  <span>{dataDetail.nama_igt}</span>
                </div>
                <div className="summary-item">
                  <label>Jenis IGT</label>
                  <span>{dataDetail.jenis_igt}</span>
                </div>
                <div className="summary-item">
                  <label>KL</label>
                  <span>{dataDetail.kl}</span>
                </div>
                <div className="summary-item">
                  <label>Status Verifikasi</label>
                  <span className={`status-badge status-${dataDetail.status.toLowerCase().replace(' ', '-')}`}>
                    {dataDetail.status}
                  </span>
                </div>
                {dataDetail.jenis_igt === 'Renaksi PKSP' && (
                  <div className="summary-item">
                    <label>Status Publikasi</label>
                    {dataDetail.status_publikasi ? (
                      <span className={`status-badge status-${dataDetail.status_publikasi.toLowerCase().replace(/ /g, '-')}`}>
                        {dataDetail.status_publikasi}
                      </span>
                    ) : (
                      <span className="status-badge" style={{ backgroundColor: '#6c757d', color: '#fff' }}>-</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Data Capaian Section */}
            {(capaianData.length > 0 || kepkaInfo) && (
              <div className="files-section">
                <h3 className="section-title">Data Capaian dan Target</h3>
                <div className="capaian-grid">
                  {/* Capaian Card */}
                  {capaianData.length > 0 && (
                    <div className="capaian-card">
                      <div className="capaian-header">
                        <h4>Capaian Tahunan</h4>
                        <span className="capaian-badge">Data Aktual</span>
                      </div>
                      <div className="capaian-list">
                        {capaianData.map((item, index) => {
                          const target = kepkaInfo?.target?.[item.tahun];
                          const persentase = target && item.nilai 
                            ? ((parseFloat(item.nilai) / parseFloat(target)) * 100).toFixed(1)
                            : null;
                          
                          return (
                            <div key={index} className="capaian-item">
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div className="capaian-year">Tahun {item.tahun}</div>
                                {persentase && (
                                  <span 
                                    className="badge" 
                                    style={{
                                      backgroundColor: parseFloat(persentase) >= 100 ? '#10b981' : parseFloat(persentase) >= 50 ? '#f59e0b' : '#ef4444',
                                      color: 'white',
                                      padding: '4px 10px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      width: 'fit-content',
                                      borderRadius: '12px'
                                    }}
                                  >
                                    {persentase}%
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <div className="capaian-value">
                                  <span className="value-number">{item.nilai}</span>
                                  <span className="value-unit">{item.satuan || 'Unit'}</span>
                                </div>
                                {target && (
                                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                                    Target: <strong>{target}</strong>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Target Card */}
                  {kepkaInfo && (
                    <div className="capaian-card">
                      <div className="capaian-header">
                        <h4>Target Capaian</h4>
                        <span className="capaian-badge kepka-badge">{kepkaInfo.no}</span>
                      </div>
                      
                      {kepkaInfo.target && (
                        <div className="target-section">
                          <div className="capaian-list">
                            {Object.entries(kepkaInfo.target)
                              .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
                              .map(([tahun, nilai]) => (
                              <div key={tahun} className="capaian-item target-item">
                                <div className="capaian-year">Tahun {tahun}</div>
                                <div className="capaian-value">
                                  <span className="value-number">{nilai}</span>
                                  <span className="value-unit">{kepkaInfo.satuan || 'Unit'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Files Section */}
            <div className="files-section">
              <h3 className="section-title">Dokumen Hasil Verifikasi</h3>
              <div className="files-grid">
                <div className="file-card">
                  <div className="file-icon">
                    <Download size={24} />
                  </div>
                  <div className="file-info">
                    <h4>Rekomendasi Hasil Verifikasi</h4>
                    {filerekom ? (
                      <a
                        href={`${config.API_URL}/downloadsurat/${btoa(filerekom.split('/').pop())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-download-link"
                      >
                        Unduh Dokumen
                      </a>
                    ) : (
                      <span className="file-unavailable">File tidak tersedia</span>
                    )}
                  </div>
                </div>

                {/* Rekomendasi Integrasi */}
                <div className="file-card">
                  <div className="file-icon">
                    <Download size={24} />
                  </div>
                  <div className="file-info">
                    <h4>Rekomendasi Integrasi dan Berbagi Pakai</h4>
                    {fileba ? (
                      <a
                        href={`${config.API_URL}/downloadsurat/${btoa(fileba.split('/').pop())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-download-link"
                      >
                        Unduh Dokumen
                      </a>
                    ) : (
                      <span className="file-unavailable">File tidak tersedia</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* History Dokumen Catatan Verifikator */}
            <div className="files-section">
              <h3 className="section-title">
                <MessageSquare size={18} style={{ marginRight: '8px' }} />
                History Dokumen Catatan Verifikasi ({verificationDocs.length})
              </h3>
              {verificationDocs.length > 3 ? (
                // List format untuk lebih dari 3 dokumen
                <div className="verification-docs-list">
                  {verificationDocs.map((doc, i) => (
                    <div key={doc.index} className="verification-doc-item">
                      <div className="doc-item-icon">
                        <Download size={18} />
                      </div>
                      <div className="doc-item-info">
                        <h5>{doc.name || `Catatan ${i + 1}`}</h5>
                        <p className="doc-verifier">Verifikator: {dataDetail.fullname}</p>
                      </div>
                      <button
                        onClick={() => downloadVerificationDoc(doc.index)}
                        className="doc-download-btn"
                      >
                        <Download size={14} />
                        Unduh
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                // Card format untuk 3 dokumen atau kurang
                <div className="files-grid">
                  {verificationDocs.length > 0 ? (
                    verificationDocs.map((doc, i) => (
                      <div key={doc.index} className="file-card">
                        <div className="file-icon">
                          <Download size={24} />
                        </div>
                        <div className="file-info">
                          <h4>{doc.name || `Catatan ${i + 1}`}</h4>
                          <p className="verifier-name">Verifikator: {dataDetail.fullname}</p>
                          <button
                            onClick={() => downloadVerificationDoc(doc.index)}
                            className="file-download-link"
                          >
                            Unduh Dokumen
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Tampilan jika tidak ada catatan dari verifikator
                    <div className="file-card is-empty">
                      <div className="file-icon">
                        <MessageSquare size={24} />
                      </div>
                      <div className="file-info">
                        <h4>Tidak Ada Catatan Tambahan</h4>
                        <span className="file-unavailable">Verifikator tidak melampirkan dokumen catatan.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <UnsealProgressModal
              isOpen={isUnsealModalOpen}
              onClose={closeUnsealModal}
              onComplete={handleUnsealComplete}
              noTiket={selectedTicket.noTiket}
              namaIgt={selectedTicket.namaIgt}
              mode="unseal"
            />
            {/* Detail Table */}
            <DataDetailTable
              dataDetail={dataDetail}
              catatan={catatan}
              config={config}
              onChecklistView={handleChecklistViewOpen}
              checklistStatus={checklistStatus}
              availableChecklists={availableChecklists}
              onViewMetadata={handleViewMetadata}
            />
          </div>
        )}
      </div>

      {/* Survey Modal */}
      <SurveyModal
        isOpen={showSurvey}
        onClose={() => setShowSurvey(false)}
        onSubmit={handleSurveySubmit}
        ticketNumber={dataDetail?.no_tiket}
      />

      {/* Scroll to Top Button */}
      {isVisible && (
        <button onClick={scrollToTop} className="scroll-to-top">
          <ArrowUp size={24} />
        </button>
      )}

      {/* Checklist View Modal */}
      <DataChecklistView
        isOpen={isChecklistViewOpen}
        onClose={handleChecklistViewClose}
        documentType={selectedDocumentType}
        noTiket={dataDetail?.no_tiket}
      />

      {/* Metadata Viewer Modal */}
      <MetadataEditor
        isOpen={isMetadataViewerOpen}
        onClose={handleMetadataViewerClose}
        metadataFile={metadataFileUrl}
        readOnly={true}
        mode="view"
        noTiket={dataDetail?.no_tiket}
      />
    </DashboardLayout>
  );
};

export default RekapDataDetailsPage;